import React, { useState, useRef, useEffect } from 'react';

interface MultiSelectOption {
    code: string;
    label: string;
}

interface MultiSelectProps {
    options: MultiSelectOption[];
    value: string[];
    onChange: (value: string[]) => void;
    placeholder?: string;
    className?: string;
    allLabel?: string;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
    options,
    value,
    onChange,
    placeholder = 'Select...',
    className = '',
    allLabel = 'All'
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Helper to check if "all" is selected (empty array or all codes present)
    const isAllSelected = () => value.length === 0 || value.length === options.length;

    // Helper to check if an individual option is selected
    const isOptionSelected = (optionCode: string) => {
        // If empty array (all selected by default), treat as all selected
        if (value.length === 0) return true;
        return value.includes(optionCode);
    };

    const handleToggle = (optionCode: string) => {
        // If currently showing "all selected" (empty array)
        if (value.length === 0) {
            // Deselect this one option (select all others)
            const allOtherCodes = options.filter(opt => opt.code !== optionCode).map(opt => opt.code);
            onChange(allOtherCodes);
        } else if (value.includes(optionCode)) {
            // Remove from selection
            const newValue = value.filter(v => v !== optionCode);
            // If removing this would leave all selected, use empty array
            if (newValue.length === options.length - 1) {
                onChange([]);
            } else {
                onChange(newValue);
            }
        } else {
            // Add to selection
            const newValue = [...value, optionCode];
            // If adding this makes all selected, use empty array
            if (newValue.length === options.length) {
                onChange([]);
            } else {
                onChange(newValue);
            }
        }
    };

    const handleSelectAll = () => {
        if (isAllSelected()) {
            // Deselect all - select just the first item to show something is NOT all selected
            onChange(options.length > 0 ? [options[0].code] : []);
        } else {
            // Select all - use empty array
            onChange([]);
        }
    };

    const getDisplayText = () => {
        if (isAllSelected()) {
            return allLabel;
        }
        if (value.length === 1) {
            const selected = options.find(opt => opt.code === value[0]);
            return selected?.label || placeholder;
        }
        return `${value.length} selected`;
    };

    return (
        <div ref={dropdownRef} className={`relative ${className}`}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full h-8 text-xs bg-background/80 border-border/50 rounded-md border px-3 py-2 text-left flex items-center justify-between hover:bg-background"
            >
                <span className="truncate">{getDisplayText()}</span>
                <svg
                    className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-background border border-border/50 rounded-md shadow-lg max-h-60 overflow-auto">
                    {/* Select All option */}
                    <label className="flex items-center px-3 py-2 hover:bg-accent cursor-pointer border-b border-border/50">
                        <input
                            type="checkbox"
                            checked={isAllSelected()}
                            onChange={handleSelectAll}
                            className="mr-2 h-4 w-4 rounded border-border"
                        />
                        <span className="text-sm font-medium">{allLabel}</span>
                    </label>

                    {/* Individual options */}
                    {options.map((option) => (
                        <label
                            key={option.code}
                            className="flex items-center px-3 py-2 hover:bg-accent cursor-pointer"
                        >
                            <input
                                type="checkbox"
                                checked={isOptionSelected(option.code)}
                                onChange={() => handleToggle(option.code)}
                                className="mr-2 h-4 w-4 rounded border-border"
                            />
                            <span className="text-sm">{option.label}</span>
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MultiSelect;

